import type { AnchorHTMLAttributes, ImgHTMLAttributes, MouseEvent } from 'react'
import { useAttachmentSrc } from './hooks/useAttachmentSrc'
import { isPrivateAttachmentUrl, openAttachmentInTab } from './services/private-attachment'

/**
 * Attachment images and links for places that used a message's URL as it is
 * (VAPT 2026-10). A private chat file's URL names it but does not open it; these
 * trade it for a signed link first (services/private-attachment.ts). Any other
 * URL behaves as before. `meetingId` resolves a meeting chat's file from that
 * meeting's record, for pages showing a past meeting.
 */

type ImgProps = Omit<ImgHTMLAttributes<HTMLImageElement>, 'src'> & {
  src?: string | null
  meetingId?: string
}

/** An attachment's `<img>`; a same-size blank until its link is in hand. */
export function AttachmentImg({ src, meetingId, ...rest }: ImgProps) {
  const resolved = useAttachmentSrc(src, { meetingId })
  if (!resolved) return <span aria-hidden className={`inline-block bg-muted ${rest.className ?? ''}`} />
  return <img {...rest} src={resolved} />
}

type LinkProps = AnchorHTMLAttributes<HTMLAnchorElement> & {
  href: string
  meetingId?: string
}

/**
 * A link to an attachment, opening in a new tab. A private one is opened with
 * a link fetched in the click (openAttachmentInTab); the plain `href` stays for
 * copy and middle-click, where it shows the CDN's refusal rather than the file.
 */
export function AttachmentLink({ href, meetingId, onClick, children, ...rest }: LinkProps) {
  const handleClick = (e: MouseEvent<HTMLAnchorElement>) => {
    onClick?.(e)
    if (e.defaultPrevented || !isPrivateAttachmentUrl(href)) return
    e.preventDefault()
    openAttachmentInTab(href, { meetingId })
  }
  return (
    <a href={href} target="_blank" rel="noopener noreferrer" {...rest} onClick={handleClick}>
      {children}
    </a>
  )
}
